<template>
  <SlideIn :open="isOpen" heading="Added to bag" @close="isOpen = false">
    <template #content>
      <div class="product">
        <div class="product__image-wrapper">
          <Picture :images="firstImage" />
        </div>
        <div class="product__details">
          <div class="product__title">{{ product.title }}</div>
          <div v-if="productHasMultipleVariants" class="product__variant">
            <span class="variant-title">{{ variant.title }}</span>
            <button
              class="edit-variant-button"
              type="button"
              @click="handleEditVariant"
            >
              Edit size
            </button>
          </div>
          <div class="product__price">
            {{
              $filters.toCurrency(
                variant.priceV2.amount,
                variant.priceV2.currencyCode,
                0
              )
            }}
          </div>
          <div class="product__quantity">
            <NumberStepper
              :count="quantity"
              :max="maxQuantity"
              @change="handleQuantityChange"
            />
            <button
              class="remove-product-button"
              type="button"
              @click="handleRemoveCartItem"
            >
              Remove
            </button>
          </div>
        </div>
      </div>

      <NostoProductRecommendationsGrid
        :recommendations="nostoProductRecommendationsProduct"
        :placement="nostoProductRecommendationsProductId"
        heading="You might also like"
      />
      <NostoProductRecommendationsGrid
        :recommendations="nostoProductRecommendationsBehaviour"
        :placement="nostoProductRecommendationsBehaviourId"
        heading="An edit just for you"
      />
    </template>
    <template #footer>
      <div class="button-group">
        <div class="cart-price">
          <span class="cart-price__heading">Subtotal</span>
          <span class="cart-price__value">
            {{ $filters.toCurrency(subtotal.amount, subtotal.currencyCode, 0) }}
          </span>
        </div>
        <DeliveryEstimatorNoInput
          v-if="isEDDEnabled"
          class="estimate-delivery-date"
        />
        <Button full-width theme="outline" @click="handleContinueShopping"
          >Continue shopping</Button
        >
        <Button full-width @click="handleGoToBag"> Go to bag </Button>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import { unwrapNodes } from "@/helpers/graphql";
import { mapState, mapActions, mapGetters } from "vuex";
import Picture from "@/components/Picture.vue";
import SlideIn from "@/components/SlideIn.vue";
import Button from "@/components/Button.vue";
import NumberStepper from "@/components/NumberStepper";
import NostoProductRecommendationsGrid from "@/components/NostoProductRecommendationsGrid.vue";
import DeliveryEstimatorNoInput from "@/components/product-display/DeliveryEstimatorNoInput.vue";
import { regionFeatureToggles } from "@/config.js";

export default {
  name: "AddedToBag",
  components: {
    NumberStepper,
    Picture,
    Button,
    SlideIn,
    NostoProductRecommendationsGrid,
    DeliveryEstimatorNoInput,
  },
  props: {
    variant: {
      type: Object,
      default: () => {},
    },
    open: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:open", "editVariant"],
  data() {
    return {
      quantity: this.cartLine?.quantity,
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsBehaviour: null,
      nostoProductRecommendationsProductId: null,
      nostoProductRecommendationsBehaviourId: null,
    };
  },
  computed: {
    ...mapState("add-to-bag", ["product"]),
    ...mapState("cart", ["cart"]),
    ...mapGetters("app", ["currencyCode"]),
    firstImage() {
      const images = unwrapNodes(this.product.images.edges);
      return images ? images[0] : null;
    },
    isOpen: {
      get() {
        return this.open;
      },
      set(value) {
        return this.$emit("update:open", value);
      },
    },
    subtotal() {
      return this.cart.estimatedCost.subtotalAmount;
    },
    cartLine() {
      const lines = unwrapNodes(this.cart?.lines.edges) || [];

      return lines.find((line) => {
        return line.merchandise.id === this.variant.id;
      });
    },
    maxQuantity() {
      return this.cartLine?.merchandise.quantityAvailable;
    },
    productHasMultipleVariants() {
      const variants = unwrapNodes(this.product?.variants.edges) || [];
      return variants.length > 1;
    },
    isEDDEnabled() {
      if (this.currencyCode) {
        return regionFeatureToggles[this.currencyCode].eddEnabled || false;
      }
      return false;
    },
  },
  watch: {
    open(val) {
      console.log("IS OPEN", val);
      if (val) {
        this.sendNosto();
      }
    },
  },
  methods: {
    ...mapActions("cart", [
      "removeCartItem",
      "updateCartItem",
      "setCartOpen",
      "checkCartRules",
    ]),
    handleGoToBag() {
      this.isOpen = false;
      this.setCartOpen(true);
    },
    async handleRemoveCartItem() {
      try {
        await this.removeCartItem(this.cartLine.id);
        this.$emit("update:open", false);
      } catch (error) {
        console.log(error);
      }
    },
    async handleQuantityChange(val) {
      console.log("VALUE", val);

      if (val > this.quantity) {
        const cartRuleResults = await this.checkCartRules(this.product.id);
        if (!cartRuleResults) {
          return;
        }
      }

      this.quantity = val;

      try {
        await this.updateCartItem({
          id: this.cartLine.id,
          quantity: val,
          merchandiseId: this.variant.id,
        });
      } catch (error) {
        console.log(error);
      }
    },
    handleEditVariant() {
      this.$emit("editVariant", {
        id: this.variant.id,
        quantity: this.quantity,
      });
    },
    handleContinueShopping() {
      this.isOpen = false;
    },
    sendNosto() {
      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewCart()
          .setPlacements(["cartpage-nosto-1", "cartpage-nosto-2"])
          .load()
          .then((data) => {
            console.log(data.recommendations["cartpage-nosto-1"]);
            console.log(data.recommendations["cartpage-nosto-2"]);
            this.nostoProductRecommendationsProduct =
              data.recommendations["cartpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["cartpage-nosto-1"]?.result_id || null;
            this.nostoProductRecommendationsBehaviour =
              data.recommendations["cartpage-nosto-2"]?.products || null;
            this.nostoProductRecommendationsBehaviourId =
              data.recommendations["cartpage-nosto-2"]?.result_id || null;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  font-size: 12px;
  font-weight: 300;
  margin-bottom: 20px;
  font-family: $heading-font-family;
}

.product {
  display: flex;
  font-size: 9px;
  font-weight: 600;

  &__image-wrapper {
    flex: 1 0 40%;
    margin-right: 20px;
  }

  &__image {
    max-width: 100%;
    height: auto;
  }

  &__details {
    flex: 1 0 calc(60% - 20px);
  }

  &__title {
    @include t__editorial-label-10;
    margin-bottom: 10px;
  }

  &__variant {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
    @include t__utility-label-20;
  }

  &__price {
    margin-bottom: 12px;
    @include t__utility-label-20;
  }

  &__quantity {
    display: flex;
    justify-content: space-between;
  }
}

.cart-price {
  display: flex;
  justify-content: space-between;
  @include t__utility-label-30;
  margin-bottom: 20px;
}

.edit-variant-button,
.remove-product-button {
  outline: none;
  background: none;
  border: none;
  @include t__utility-body-10;
}

.button-group {
  ::v-deep(.button + .button) {
    margin-top: 10px;
  }
}

.estimate-delivery-date {
  padding: 0;
  margin-bottom: 20px;
}
</style>
